'use strict';

/**
 * Pict-Provider-IssueBoardAPI
 * ---------------------------
 * Browser-side client for the issue endpoints that auto-CRUD cannot serve: the dashboard (kanban
 * lanes + the standard cuts + call volume), the kanban move, the issue<->work-item links, and
 * spawning a vision from an issue. Basic issue CRUD (create, read, update, list) goes through the
 * generic EntityAPI; this provider is the rest. Tenancy + access are enforced server-side.
 *
 * AppData.IssueBoard = { Lanes, Mine, RecentlyClosed, Counts, CallVolume, Loading, Error }
 *   card = { IDIssue, IssueNumber, Title, Status, Priority, Severity, TypeName, TypeColor,
 *            AssigneeName, CustomerName, FromCall, ResolvedDate, SortOrder }
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'IssueBoardAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

const _SORT_STEP = 100; // sparse SortOrder so a drop rarely renumbers a lane

class PictProviderIssueBoardAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	store()
	{
		if (!this.fable.AppData.IssueBoard)
		{
			this.fable.AppData.IssueBoard =
			{
				Lanes: [], Mine: [], RecentlyClosed: [],
				Counts: { Total: 0, Open: 0, Mine: 0, ClosedRecently: 0 },
				CallVolume: { Last7: 0, Last30: 0, Total: 0, OpenFromCalls: 0 },
				Loading: false, Error: null
			};
		}
		return this.fable.AppData.IssueBoard;
	}

	_url(pPath) { let tmpPrefix = this.routePrefix; if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; } return tmpPrefix + pPath.replace(/^\//, ''); }
	// Recovery-covered read/write; both preserve the null-on-error contract callers rely on.
	_get(pPath) { return libFrameworkClient.getJSON(this.fable, this._url(pPath)).catch(() => null); }
	_post(pPath, pBody) { return libFrameworkClient.postJSON(this.fable, this._url(pPath), pBody || {}).catch(() => null); }

	// The whole issues dashboard in one read; mirrored into AppData.IssueBoard.
	dashboard()
	{
		let tmpStore = this.store();
		tmpStore.Loading = true;
		return this._get('Issues/Dashboard').then((pBody) =>
		{
			if (pBody)
			{
				tmpStore.Lanes = pBody.Lanes || [];
				tmpStore.Mine = pBody.Mine || [];
				tmpStore.RecentlyClosed = pBody.RecentlyClosed || [];
				tmpStore.Counts = pBody.Counts || tmpStore.Counts;
				tmpStore.CallVolume = pBody.CallVolume || tmpStore.CallVolume;
				tmpStore.Error = null;
			}
			else { tmpStore.Error = 'Could not load the issues dashboard.'; }
			tmpStore.Loading = false;
			return tmpStore;
		}).catch((pErr) => { tmpStore.Loading = false; tmpStore.Error = (pErr && pErr.message) || 'load failed'; return tmpStore; });
	}

	// Next sparse SortOrder for appending a card to the end of a lane.
	nextSortOrder(pLaneKey)
	{
		let tmpStore = this.store();
		let tmpMax = 0;
		for (let i = 0; i < tmpStore.Lanes.length; i++)
		{
			if (tmpStore.Lanes[i].Key !== pLaneKey) { continue; }
			let tmpCards = tmpStore.Lanes[i].Cards || [];
			for (let j = 0; j < tmpCards.length; j++) { if ((tmpCards[j].SortOrder || 0) > tmpMax) { tmpMax = tmpCards[j].SortOrder; } }
		}
		return tmpMax + _SORT_STEP;
	}

	// Move an issue to a lane: PUT the Status + SortOrder (Issue has no WorkflowStatus column), exactly
	// like the Feature/WorkItem board, so the server audit logs the transition and stamps ResolvedDate.
	moveCard(pID, pToStatus, pSortOrder)
	{
		let tmpBody = { IDIssue: pID, Status: pToStatus, SortOrder: pSortOrder };
		// EntityProvider.updateEntity -> the shared RestClient's putJSON (recovery-covered).
		return libFrameworkClient.updateEntity(this.fable, 'Issue', tmpBody);
	}

	// Issue<->WorkItem links use the generic join (IssueWorkItemJoin via the 'link' relation render);
	// this provider only owns the dashboard, the kanban move, and spawning a vision.
	spawnVision(pID, pDraft) { return this._post('Issue/' + encodeURIComponent(pID) + '/SpawnVision', pDraft || {}); }
	// Promote a piece of Feedback into a WorkItem: the server links WorkItem.IDSourceFeedback and
	// carries the target (Vision|Module|Feature) chosen in the spawn dialog.
	spawnWorkItem(pIDFeedback, pDraft) { return this._post('Feedback/' + encodeURIComponent(pIDFeedback) + '/SpawnWorkItem', pDraft || {}); }
	// Promote an Issue into a WorkItem: the server reverse-links via IssueWorkItemJoin (Addresses).
	spawnWorkItemFromIssue(pIDIssue, pDraft) { return this._post('Issue/' + encodeURIComponent(pIDIssue) + '/SpawnWorkItem', pDraft || {}); }
	// Link many Issues to one WorkItem at once (idempotent). pRelationType defaults to Addresses.
	linkIssuesToWorkItem(pIDWorkItem, pIDIssues, pRelationType) { return this._post('WorkItem/' + encodeURIComponent(pIDWorkItem) + '/Issues/Link', { IDIssues: pIDIssues || [], RelationType: pRelationType || 'Addresses' }); }
	// Link many Feedback to one WorkItem at once (idempotent). pRelationType defaults to Drives. The
	// server keeps WorkItem.IDSourceFeedback as the primary (first) link for backward compatibility.
	linkFeedbackToWorkItem(pIDWorkItem, pIDFeedbacks, pRelationType) { return this._post('WorkItem/' + encodeURIComponent(pIDWorkItem) + '/Feedback/Link', { IDFeedbacks: pIDFeedbacks || [], RelationType: pRelationType || 'Drives' }); }
	assignees() { return this._get('Issues/Assignees').then((pBody) => (pBody && pBody.Assignees) || []).catch(() => []); }
}

module.exports = PictProviderIssueBoardAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
